import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { Attribute, RatingSet } from "../models";

/**
 * Collapsible panel that shows per-attribute deltas between the active dataset
 * and every other dataset. Hidden by default; the toggle button reveals it.
 *
 * @prop datasets     - All datasets in the current state.
 * @prop activeIndex  - Index of the dataset treated as the reference ("you").
 * @prop attrs        - Ordered attribute list for the active version.
 */
@customElement("bab-comparison-stats")
export class BabComparisonStats extends LitElement {
  static styles = css`
    :host {
      display: block;
      margin-top: 16px;
    }

    .toggle-btn {
      padding: 7.5px 18.5px;
      background: var(--bab-button-secondary-bg, #fff);
      color: var(--bab-button-secondary-text, #396acc);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border, #e5e7eb);
      border-radius: var(--bab-radius, 12px);
      font-size: 0.9rem;
      font-weight: var(--bab-font-weight, 600);
      font-family: inherit;
      cursor: pointer;
      transition: background 0.15s, transform 0.1s;
    }

    .toggle-btn:hover {
      background: var(--bab-bg, #f0f4f8);
    }

    .toggle-btn:active {
      transform: scale(0.97);
    }

    .toggle-btn:focus-visible {
      outline: 2px solid var(--bab-accent, #4a90e2);
      outline-offset: 2px;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 16px;
      margin-top: 12px;
    }

    .comparison-card {
      background: var(--bab-card-bg, #fff);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border, #e5e7eb);
      border-radius: var(--bab-radius, 12px);
      overflow: hidden;
      box-shadow:
        0 1px 3px rgba(0, 0, 0, 0.08),
        0 4px 12px rgba(0, 0, 0, 0.04);
    }

    .comparison-header {
      padding: 10px 16px;
      background: var(--bab-bg, #f0f4f8);
      border-bottom: var(--bab-border-width, 1.5px) solid var(--bab-border, #e5e7eb);
      font-size: 0.72rem;
      font-weight: var(--bab-font-weight, 600);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bab-text-secondary, #646b78);
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .color-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
      border: 1px solid rgba(0, 0, 0, 0.1);
    }

    .delta-row {
      display: flex;
      align-items: center;
      padding: 7px 14px;
      border-bottom: 1px solid var(--bab-border, #e5e7eb);
      gap: 10px;
    }

    .delta-row:last-child {
      border-bottom: none;
    }

    .delta-badge {
      min-width: 40px;
      text-align: center;
      font-weight: var(--bab-font-weight, 600);
      font-size: 0.78rem;
      padding: 2px 6px;
      border-radius: 6px;
      flex-shrink: 0;
    }

    .delta-badge--pos {
      background: var(--bab-delta-pos-bg, #dcfce7);
      color: var(--bab-delta-pos, #166534);
    }

    .delta-badge--neg {
      background: var(--bab-delta-neg-bg, #fee2e2);
      color: var(--bab-delta-neg, #991b1b);
    }

    .delta-badge--tie {
      background: var(--bab-border, #e5e7eb);
      color: var(--bab-text-secondary, #646b78);
    }

    .delta-badge--null {
      color: var(--bab-text-secondary, #646b78);
      font-size: 0.85rem;
    }

    .attr-name {
      flex: 1;
      font-size: 0.85rem;
      color: var(--bab-text, #1a1a2e);
    }

    .score-pair {
      font-size: 0.75rem;
      color: var(--bab-text-secondary, #646b78);
      flex-shrink: 0;
      font-variant-numeric: tabular-nums;
    }
  `;

  @property({ type: Array }) datasets: RatingSet[] = [];
  @property({ type: Number }) activeIndex = 0;
  @property({ type: Array }) attrs: Attribute[] = [];

  @state() private _expanded = false;

  render() {
    const comparisons = this.datasets
      .map((ds, i) => ({ ds, i }))
      .filter(({ i }) => i !== this.activeIndex);

    if (comparisons.length === 0) return nothing;

    const active = this.datasets[this.activeIndex];
    const activeName = active?.name?.trim() || "You";

    return html`
      <button
        class="toggle-btn"
        @click="${() => { this._expanded = !this._expanded; }}"
        aria-expanded="${this._expanded}"
      >
        ${this._expanded ? "Hide" : "Show"} comparison stats
      </button>

      ${this._expanded
        ? html`
            <div class="stats-grid">
              ${comparisons.map(({ ds, i }) => {
                const compName = ds.name?.trim() || `Dataset ${i + 1}`;
                return html`
                  <div class="comparison-card">
                    <div class="comparison-header">
                      <span
                        class="color-dot"
                        style="background:${ds.color ?? "#888"}"
                      ></span>
                      ${activeName} vs ${compName}
                    </div>
                    ${this.attrs.map((attr) => {
                      const av = active?.ratings[attr.id] ?? null;
                      const cv = ds.ratings[attr.id] ?? null;
                      const delta = av !== null && cv !== null ? av - cv : null;
                      const badgeClass =
                        delta === null
                          ? "delta-badge--null"
                          : delta > 0
                            ? "delta-badge--pos"
                            : delta < 0
                              ? "delta-badge--neg"
                              : "delta-badge--tie";
                      const badgeText =
                        delta === null
                          ? "—"
                          : delta > 0
                            ? `+${delta}`
                            : delta === 0
                              ? "="
                              : `${delta}`;
                      return html`
                        <div class="delta-row">
                          <span class="delta-badge ${badgeClass}"
                            >${badgeText}</span
                          >
                          <span class="attr-name">${attr.name}</span>
                          ${av !== null && cv !== null
                            ? html`<span class="score-pair"
                                >${av} / ${cv}</span
                              >`
                            : nothing}
                        </div>
                      `;
                    })}
                  </div>
                `;
              })}
            </div>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bab-comparison-stats": BabComparisonStats;
  }
}
